import styled from 'styled-components';
import { fontSizes, letterSpacings, lineHeights, sizes, spacings } from './Input.constants';

interface ISizeProps {
  size: 's' | 'm';
  growVertically?: boolean;
  withIcon?: boolean;
  width?: number;
}

interface ITtypeProps {
  type: 'primary' | 'secondary' | 'minimal';
  disabled: boolean;
  hasError: boolean;
  hasWarning?: boolean;
}

export const SField = styled.div<ISizeProps>`
  display: flex;
  flex-direction: column;
  flex-basis: ${({ width }) => (width ? `${width}px` : 'auto')};
  flex-shrink: 0;
  flex-grow: ${({ growVertically, width }) => (growVertically || !width ? '1' : '0')};
  margin-bottom: 5px;
  max-width: ${({ size }) => sizes[size]};
  padding-bottom: ${({ size }) => spacings[size].bottom};
  position: relative;

  .pb-0 {
    padding-bottom: 0;
  }

  .mb-0 {
    margin-bottom: 0;
  }

  &.search-criteria-field {
    max-width: 100%;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  &.read-only {
    input:disabled {
      background-color: transparent;
      color: ${({ theme }) => theme.colors.primaryText};
      opacity: var(--read-only-opacity);
      padding-left: 0;
    }

    label {
      opacity: var(--read-only-opacity);
    }

    textarea:disabled {
      background-color: transparent;
      opacity: var(--read-only-opacity);
    }
  }

  &.hidden {
    display: none;
  }
`;

export const SLabel = styled.label<ISizeProps>`
  color: ${({ theme }) => theme.colors.tertiaryText};
  font-family: ${({ theme }) => theme.fonts.medium};
  font-size: ${({ size }) => fontSizes.label[size]};
  font-weight: 500;
  letter-spacing: ${({ size }) => letterSpacings.label[size]};
  line-height: ${({ size }) => lineHeights.label[size]};
  margin-bottom: 5px;
  text-transform: uppercase;
`;

export const SInfo = styled.div`
  margin-top: 5px;
  font-size: 10px;
  letter-spacing: 0.1px;
  color: ${({ theme }) => theme.colors.tertiaryText};

  &.read-only {
    display: none;
  }
`;

export const SInputContainer = styled.div<ISizeProps & ITtypeProps>`
  flex-grow: ${({ growVertically }) => (growVertically ? '1' : '0')};

  input,
  textarea {
    background-color: ${({ theme, type }) =>
      type === 'primary' ? theme.colors.secondaryBackground : theme.colors.quaternaryBackground};
    border-radius: 0.156rem;
    border: ${({ theme, hasError, hasWarning }) =>
      hasError ? `1px solid ${theme.colors.error}` : hasWarning ? `1px solid ${theme.colors.warningBorder}` : 'none'};
    color: ${({ theme }) => theme.colors.senaryText};
    font-family: ${({ theme }) => theme.fonts.regular};
    font-size: ${({ size }) => fontSizes.input[size]};
    letter-spacing: ${({ size }) => letterSpacings.input[size]};
    line-height: ${({ size }) => lineHeights.input[size]};
    outline: none;
    padding: ${({ size }) => `${spacings[size].vertical} ${spacings[size].horizontal}`};
    padding-right: ${({ size, withIcon }) => (withIcon ? spacings[size].withIcon : spacings[size].horizontal)};
    transition: box-shadow 0.3s linear;
    width: 100%;

    &::placeholder {
      color: ${({ theme }) => theme.colors.senaryText};
    }

    &:hover {
      box-shadow: ${({ theme, hasError }) => `0 0 0 2px ${hasError ? theme.colors.error : theme.colors.icon}`};
    }

    &:focus {
      box-shadow: ${({ theme, hasError }) => `0 0 0 1px ${hasError ? theme.colors.error : theme.colors.icon}`};
    }

    &:hover {
      box-shadow: ${({ theme, hasWarning }) =>
        `0 0 0 2px ${hasWarning ? theme.colors.warningBorder : theme.colors.icon}`};
    }

    &:focus {
      box-shadow: ${({ theme, hasWarning }) =>
        `0 0 0 1px ${hasWarning ? theme.colors.warningBorder : theme.colors.icon}`};
    }

    &:disabled {
      background-color: ${({ theme }) => theme.colors.quaternaryBackground};
      box-shadow: none;
    }
  }

  textarea {
    min-height: 100%;
    resize: none;
  }

  input.minimal,
  textarea.minimal {
    background-color: ${({ theme }) => theme.colors.secondaryBackground};
  }
`;

export const SError = styled.span<ISizeProps>`
  bottom: 0;
  color: ${({ theme }) => theme.colors.error};
  font-family: ${({ theme }) => theme.fonts.regular};
  font-size: ${({ size }) => fontSizes.error[size]};
  letter-spacing: ${({ size }) => letterSpacings.error[size]};
  line-height: ${({ size }) => lineHeights.error[size]};
  margin-bottom: -10px;
  position: absolute;
`;

export const SIconContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: 0.938rem;
  top: 50%;
  transform: translateY(-50%);
`;
